import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getApplicationSettings } from 'src/modules/applications/slices';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { selectProfile } from 'src/modules/profile/slices';
import {
  bookingEmbedUrl,
  bookingRedirectDelayMs,
  isCallBookedMessage,
  signupCompletedRoute,
} from '../helpers';

/**
 * The call this application offers, ready to be framed, or null where it offers none — an unusable link and an
 * unset one alike. Also what the funnel asks before sending anybody to the screen, so the two cannot disagree
 * about whether there is a call to offer.
 */
export const useBookingEmbedUrl = (): string | null => {
  const settings = useSelector(getApplicationSettings);
  const { profile } = useSelector(selectProfile);

  return useMemo(
    () => bookingEmbedUrl(settings?.selfService?.bookingUrl, {
      firstName: profile?.firstName ?? null,
      lastName: profile?.lastName ?? null,
      email: profile?.email ?? null,
    }),
    [profile?.email, profile?.firstName, profile?.lastName, settings?.selfService?.bookingUrl],
  );
};

export const useSignupBookCallViewController = () => {
  const navigate = useNavigate();
  const companyId = useSelector(getCurrentCompanyId);
  const settings = useSelector(getApplicationSettings);
  const embedUrl = useBookingEmbedUrl();
  const redirectDelayMs = bookingRedirectDelayMs(settings?.selfService?.bookingRedirectSeconds);
  const [booked, setBooked] = useState(false);
  const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (isCallBookedMessage(event, embedUrl)) setBooked(true);
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [embedUrl]);

  const cancelRedirect = useCallback(() => {
    if (redirectTimer.current == null) return;
    clearTimeout(redirectTimer.current);
    redirectTimer.current = null;
  }, []);

  const done = useCallback(() => {
    cancelRedirect();
    navigate(signupCompletedRoute(companyId), { replace: true });
  }, [cancelRedirect, companyId, navigate]);

  useEffect(() => {
    if (!booked || redirectDelayMs == null) return;
    if (redirectDelayMs === 0) {
      done();
      return;
    }

    redirectTimer.current = setTimeout(done, redirectDelayMs);
    return cancelRedirect;
  }, [booked, cancelRedirect, done, redirectDelayMs]);

  // The address is in the history of everyone who has passed it, and the setting can be cleared between one
  // client being sent here and the next one opening it again.
  useEffect(() => {
    if (embedUrl == null) done();
  }, [done, embedUrl]);

  return { embedUrl, booked, done };
};
